/**
 * SupervisionFilters
 *
 * Filters: clinician, document type, status. No date range.
 * Sorting is done via table column headers (see CosignNotesTable).
 */

import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { SearchInput } from '../../../components/SearchInput';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import type { SupervisionFilters as SupervisionFiltersType } from '../types';
import { DocumentTypeLabels, DocumentReviewStatusLabels, getSupervisionQueueTypeLabel } from '../types';

interface SupervisionFiltersProps {
  filters: SupervisionFiltersType;
  onFiltersChange: (f: SupervisionFiltersType) => void;
  clinicianOptions: { value: string; label: string }[];
  hideClinicianFilter?: boolean;
  hideStatusFilter?: boolean;
  documentTypes?: (keyof typeof DocumentTypeLabels)[];
  statuses?: (keyof typeof DocumentReviewStatusLabels)[];
}

export function SupervisionFilters({
  filters,
  onFiltersChange,
  clinicianOptions,
  hideClinicianFilter = false,
  hideStatusFilter = false,
  documentTypes,
  statuses,
}: SupervisionFiltersProps): JSX.Element {
  const resetFilters = (): void => {
    onFiltersChange({
      ...filters,
      clinicianIds: [],
      documentTypes: [],
      status: [],
      riskLevel: 'all',
      search: '',
    });
  };

  const hasActiveFilters =
    filters.clinicianIds.length > 0 ||
    filters.documentTypes.length > 0 ||
    filters.status.length > 0 ||
    filters.search !== '';

  const typeKeys = documentTypes
    ?? (Object.keys(DocumentTypeLabels) as (keyof typeof DocumentTypeLabels)[]);
  const documentTypeOptions = typeKeys.map((value) => ({
    value,
    label: getSupervisionQueueTypeLabel({ documentType: value }),
  }));

  const cosignTableStatuses = statuses
    ?? (['draft', 'edits-requested', 'signed', 'cosigned'] as const);
  const statusOptions = Object.entries(DocumentReviewStatusLabels).filter(([value]) =>
    (cosignTableStatuses as readonly string[]).includes(value),
  ).map(([value, label]) => ({ value, label }));

  return (
    <Group gap="md" wrap="wrap" align="center" flex="1">
      <SearchInput
        placeholder="Search notes..."
        iconSize={14}
        value={filters.search}
        onChange={(e) => onFiltersChange({ ...filters, search: e.currentTarget.value })}
        w={280}
      />

      {hideClinicianFilter ? null : (
        <FilterMultiSelect
          allLabel="All team members"
          data={clinicianOptions}
          value={filters.clinicianIds}
          onChange={(v) => onFiltersChange({ ...filters, clinicianIds: v })}
          w={220}
        />
      )}

      <FilterMultiSelect
        allLabel="All types"
        data={documentTypeOptions}
        value={filters.documentTypes}
        onChange={(v) => onFiltersChange({ ...filters, documentTypes: v as SupervisionFiltersType['documentTypes'] })}
        w={220}
      />

      {hideStatusFilter ? null : (
        <FilterMultiSelect
          allLabel="All statuses"
          data={statusOptions}
          value={filters.status}
          onChange={(v) => onFiltersChange({ ...filters, status: v as SupervisionFiltersType['status'] })}
          w={220}
        />
      )}

      {hasActiveFilters && (
        <Tooltip label="Clear filters">
          <ActionIcon variant="light" color="gray" size="sm" onClick={resetFilters}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}
